import './approvazione-ferie.scss';

import React, { useRef, useState, useCallback, useEffect } from 'react';
import config from "devextreme/core/config";
import { DataGrid, DropDownButton, Button, CheckBox, SpeedDialAction } from 'devextreme-react';
import { Column, Toolbar, DataGridRef, DataGridTypes, Grouping, Sorting, Scrolling, Selection } from 'devextreme-react/data-grid';
import { Item } from 'devextreme-react/toolbar';
import { useApprovazioneFerieDataSource } from '../../datasources/approvazioneFerieDataSource';
import { ApprovazioneFeriePopup } from "../../components";
import { PopupApprovazioneFerieHandle } from '../../components/popup-approvazione-ferie/popupApprovazioneFerie';
import { useUserSessionContext } from '../../contexts/UserSessionContext';
import { useScreenSize, useScreenSizeClass } from '../../utils/media-query';

export default function ApprovazioneFerie() {
    const gridRef = useRef<DataGridRef>(null);
    const popupRef = useRef<PopupApprovazioneFerieHandle>(null);
    const {user} = useUserSessionContext();
    
    const { isLarge, isMedium } = useScreenSize();
    const screenSizeClass = useScreenSizeClass();

    const [selectedStatoFilter, setSelectedStatoFilter] = useState<string>("D");
    const [seletedStatoFilterLabel, setSelectedStatoFilterLabel] = useState<string>("In attesa");
    const [showAllFilter, setShowAllFilter] = useState<boolean>(false);
    const [selectedIdRichiesta, setSelectedIdRichiesta] = useState<number>(-1);

    config({
        floatingActionButtonConfig: {
            icon: "optionsgear",
            shading: true,
            position: {
                my: 'right bottom',
                at: 'right bottom',
                of: "#gridApprovazioneFerie",
                offset: "-16 -16"
            }
        }
    });

    const statoFilterOptions = [
        { text: 'Tutte', value: 'T' },
        { text: 'In attesa', value: 'D' },
        { text: 'Storico', value: 'S' }
    ];

    function handleGridRowClick(e: DataGridTypes.RowClickEvent) {
        e.event.preventDefault();
        e.event.stopPropagation();

        const idRichiesta = e.data?.id;
        if (idRichiesta == null) return;

        if (idRichiesta === selectedIdRichiesta) {
            setSelectedIdRichiesta(-1);
            gridRef.current?.instance().deselectAll();
        } else
            setSelectedIdRichiesta(idRichiesta);
    };

    function formatDate(date: Date): string {
        return date.toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
        });
    };

    function openDetails(id: number) {
        popupRef.current?.show(id);
    };
    
    const refresh = useCallback(() => {
        gridRef.current?.instance().refresh();
    }, []);

    useEffect(() => {
        gridRef.current?.instance().deselectAll();
        setSelectedIdRichiesta(-1);
    }, [isLarge, isMedium]);

    return (
        <React.Fragment>
            <div className='view-list-approvazione-ferie'>
                <div className={`view-wrapper view-wrapper-list list-page ${screenSizeClass}`}>
                    <DataGrid
                        id="gridApprovazioneFerie" ref={gridRef} key="id"
                        dataSource={useApprovazioneFerieDataSource(showAllFilter, selectedStatoFilter)}
                        showBorders rowAlternationEnabled
                        noDataText='Non ci sono richieste da visualizzare'
                        className='grid theme-dependent'
                        defaultFocusedRowIndex={0} onRowClick={handleGridRowClick}
                    >
                        <Sorting mode="single" />
                        <Grouping autoExpandAll={false} />
                        <Scrolling mode="virtual" />
                        { !isLarge && !isMedium && <Selection mode='single' /> }

                        <Toolbar>
                            <Item location='before'>
                                <div className='grid-header'>Approvazioni Richieste</div>
                            </Item>

                            <Item location='before' locateInMenu='auto'>
                                <DropDownButton
                                    text={seletedStatoFilterLabel}
                                    items={statoFilterOptions}
                                    displayExpr="text"
                                    keyExpr="value"
                                    width={120}
                                    stylingMode='text'
                                    onItemClick={(e) => {
                                        setSelectedStatoFilter(e.itemData.value);
                                        setSelectedStatoFilterLabel(e.itemData.text);
                                    }}
                                />
                            </Item>

                            <Item visible={user?.isSupervisore ?? false} locateInMenu='auto' location='before'>
                                <CheckBox
                                    defaultValue={showAllFilter}
                                    text="Visualizza tutte le richieste"
                                    onValueChanged={(e) => setShowAllFilter(e.value)}
                                />
                            </Item>

                            <Item
                                location='after'
                                locateInMenu='never'
                                showText='inMenu'
                                widget='dxButton'
                            >
                                <Button 
                                    icon='refresh'
                                    text='Refresh'
                                    stylingMode='text'
                                    onClick={refresh}
                                />
                            </Item>
                        </Toolbar>

                        <Column
                            type='buttons'
                            visible={isLarge || isMedium}
                            width={45}
                            cellRender={(cellData) => (
                                <div style={{ display: "flex" }}>
                                    <Button icon='search' hint="Dettagli" stylingMode='text' onClick={() => openDetails(cellData.data.id)} />
                                </div>
                            )}
                        />
                        <Column dataField='richiedente' />
                        <Column dataField='dataRichiesta' dataType='date' hidingPriority={0} />
                        <Column dataField='causale' hidingPriority={1} />
                        <Column 
                            caption='Tempistiche' 
                            cellRender={({ data }) => {
                                if (data.ore != null) {
                                    if (data.ore < 0)
                                        return `Storno di ${data.ore * -1} ${data.ore < -1 ? "ore" : "ora"} il ${formatDate(data.daData)}`;

                                    return `${data.ore} ${data.ore > 1 ? "ore" : "ora"} il ${formatDate(data.daData)}`;
                                } else
                                    return `Dal ${formatDate(data.daData)} al ${formatDate(data.aData)}`;
                            }}
                            hidingPriority={2}
                        />
                        <Column 
                            dataField='approvazione' caption='Stato Richiesta' groupIndex={selectedStatoFilter == "D" ? -1 : 0} 
                            sortOrder='asc'
                            sortingMethod={(a, b) => {
                                const order = ["In approvazione"];
                                const iA = order.indexOf(a);
                                const iB = order.indexOf(b);

                                // se non esiste nell'array, lo metti in fondo
                                return (iA === -1 ? 99 : iA) - (iB === -1 ? 99 : iB);
                            }}
                        />
                    </DataGrid>

                    <SpeedDialAction 
                        icon="search" label="Dettagli"
                        visible={!isLarge && !isMedium && selectedIdRichiesta > 0}
                        index={1} onClick={() => openDetails(selectedIdRichiesta)}
                    />
                </div>
            </div>

            <ApprovazioneFeriePopup ref={popupRef} onChange={refresh} />
        </React.Fragment>
    );
}